import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Grid, Skeleton, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  ChatOutlined,
  MarkEmailReadOutlined,
  PollOutlined,
  SchoolOutlined,
  TaskOutlined,
  VolunteerActivismOutlined,
} from '@mui/icons-material';
import { GET_ENGAGEMENT_REPORT } from '../../queries';
import { hasEngagementData } from '../../utilities';
import { useAwoContext } from '../AwoContextProvider';
import DateFilter from '../DateFilter';
import ErrorMessage from '../ErrorMessage';
import EmptyPageMessage from '../Feedback/EmptyPageMessage';
import DashboardStatCard from '../Dashboard/DashboardStatCard';
import ChannelBreakdownCard from './ChannelBreakdownCard';
import RelationshipTable from './RelationshipTable';
import {
  countDelta, countOf, percentOf, periodVariables, rateDelta,
} from './engagementMath';
import { relationshipsFor, RelationshipStats } from './relationships';

interface EngagementTotals {
  supported: number;
  engaged: number;
  lessons: number;
  messages: number;
  surveys: number;
  engagedLessons: number;
  engagedMessages: number;
  engagedSurveys: number;
}

interface EngagementReport {
  totals: EngagementTotals;
  byRelationship: RelationshipStats[];
}

const EMPTY: EngagementTotals = {
  supported: 0,
  engaged: 0,
  lessons: 0,
  messages: 0,
  surveys: 0,
  engagedLessons: 0,
  engagedMessages: 0,
  engagedSurveys: 0,
};

interface StatCard {
  title: string;
  text: string;
  subtext?: string;
  icon: React.ReactNode;
}

function StatCardRow({ cards, loading }: { cards: StatCard[], loading: boolean }) {
  return (
    <Grid container spacing={{ xs: 2, md: 3 }}>
      {cards.map((c) => (
        <Grid key={c.title} item xs={12} md={4}>
          <DashboardStatCard
            title={c.title}
            text={loading ? <Skeleton /> : c.text}
            subtext={loading ? '' : c.subtext}
            icon={c.icon}
          />
        </Grid>
      ))}
    </Grid>
  );
}

// Engagement tab (ticket-2542): how an AWO's pet parents used Petcademy over a period.
export default function Engagement() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const { awo, loading: awoLoading } = useAwoContext();
  const [daysAgo, setDaysAgo] = React.useState(30);
  // An AWO in its first 30 days has no completed support yet: the dashboard shows "-", and this
  // tab shows the empty screen instead. (An admin with no AWO selected always sees the report.)
  const noDataYet = !awoLoading && !!awo.id && !hasEngagementData(awo.dateCreated);

  // Switching periods drops the old data rather than showing it under the new label.
  const { loading, error, data } = useQuery<{ current: EngagementReport, previous?: EngagementReport }>(
    GET_ENGAGEMENT_REPORT,
    { skip: awoLoading || noDataYet, variables: periodVariables(daysAgo) },
  );

  if (noDataYet) {
    return (
      <Box>
        <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 } }}>
          <Typography variant="overline" component="h1">Engagement</Typography>
        </Box>
        <EmptyPageMessage />
      </Box>
    );
  }

  const busy = loading || awoLoading;
  const now = data?.current.totals || EMPTY;
  const before = data?.previous?.totals || EMPTY;
  const rate = percentOf(now.engaged, now.supported);
  const previousRate = percentOf(before.engaged, before.supported);

  const summaryCards: StatCard[] = [
    {
      title: 'Pet parents supported',
      text: `${now.supported.toLocaleString()} total`,
      subtext: countDelta(now.supported, before.supported, daysAgo),
      icon: <VolunteerActivismOutlined />,
    },
    {
      title: 'Pet parents who engaged',
      text: `${now.engaged.toLocaleString()} of ${now.supported.toLocaleString()}`,
      subtext: countDelta(now.engaged, before.engaged, daysAgo),
      icon: <TaskOutlined />,
    },
    {
      title: 'Engagement rate',
      text: `${rate}%`,
      subtext: rateDelta(rate, previousRate, daysAgo),
      icon: <MarkEmailReadOutlined />,
    },
  ];
  const channelCards: StatCard[] = [
    {
      title: 'Lessons reviewed',
      text: countOf(now.lessons, 'lesson'),
      subtext: countDelta(now.lessons, before.lessons, daysAgo),
      icon: <SchoolOutlined />,
    },
    {
      title: 'Messages sent',
      text: countOf(now.messages, 'message'),
      subtext: countDelta(now.messages, before.messages, daysAgo),
      icon: <ChatOutlined />,
    },
    {
      title: 'Surveys submitted',
      text: countOf(now.surveys, 'survey'),
      subtext: countDelta(now.surveys, before.surveys, daysAgo),
      icon: <PollOutlined />,
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="overline" component="h1">Engagement</Typography>
          {/* The Engagement tab doesn't offer "Last week". */}
          <DateFilter
            selectedDate={daysAgo}
            onChange={(e) => setDaysAgo(Number(e.target.value))}
            hiddenOptions={[7]}
          />
        </Box>
        {error ? <ErrorMessage /> : <StatCardRow cards={summaryCards} loading={busy} />}
      </Box>
      {!error && (
        <>
          <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="overline" component="h2">How they engaged</Typography>
            <StatCardRow cards={channelCards} loading={busy} />
            <ChannelBreakdownCard
              loading={busy}
              engaged={now.engaged}
              engagedLessons={now.engagedLessons}
              engagedMessages={now.engagedMessages}
              engagedSurveys={now.engagedSurveys}
            />
          </Box>
          {/* Not rendered at all below md (AC 7, QA 5.10) — hiding it with CSS would leave it in the DOM. */}
          {isDesktop && (
            <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="overline" component="h2">By relationship</Typography>
              <RelationshipTable
                loading={busy}
                daysAgo={daysAgo}
                relationships={relationshipsFor(awo)}
                current={data?.current.byRelationship}
                previous={data?.previous?.byRelationship}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
